import { DataManager } from "../model/DataManager.js";
import { ISunCalc } from "../model/ISunCalc.js";
import { Resource } from "../model/Resource.js";
import { SunCalcMeeusJs } from "../model/SunCalcMeeusJs.js";
import { Color } from "../utils/Color.js";

export class WeatherView {
    public element: HTMLDivElement;

    public overrideTimeWithNow: boolean = true;

    private _svg: SVGElement;
    private _sunCalc: ISunCalc;
    private _colorNight = ["#3D5B8B", "#293A63", "#0F0C2D", "#0B0625"];
    private _colorSunrise = ["#E49B80", "#E6A069", "#9C7673", "#7B6478"];
    private _colorDay = ["#90B8D5", "#6EA7CB", "#0976AD", "#0172AB"];
    private _colorSunset = ["#8A70A1", "#7E75A4", "#6D7BA8", "#617FAA"];
    private _debug: HTMLDivElement;
    private _date: Date = new Date();

    constructor(private _data: DataManager) {
        this.element = document.createElement("div");
        this.element.className = "weather-view";

        this._sunCalc = new SunCalcMeeusJs();
        // this._sunCalc = new SunCalcVladimir();

        $.get(Resource.asset("weather.svg"), (data) => {
            this._svg = data.documentElement;
            data.documentElement.setAttribute("preserveAspectRatio", "xMidYMax slice");
            data.documentElement.style.width = "100%";
            data.documentElement.style.height = "100%";
            this.element.appendChild(data.documentElement);

            this._debug = document.createElement("div");
            this._debug.style.position = "absolute";
            this._debug.style.top = "0";
            this._debug.style.left = "0";
            this._debug.style.color = "#FFFFFF";
            this.element.appendChild(this._debug);

            this.update(this._date);
        });
    }

    public update(date: Date): void {
        if (date)
            this._date = date;

        if (!this._svg || !date)
            return;

        /**
        * Create a dummy date.
        */
        date = new Date(date);

        /**
         * The given date has no correct time-setting (only date, no hours/minutes/seconds).
         * Set the current time (now). 
         * The year, month and day keeps the same.
         */
        if (this.overrideTimeWithNow) {
            var now = new Date();
            date.setUTCHours(now.getUTCHours());
            date.setUTCMinutes(now.getUTCMinutes());
            date.setUTCSeconds(now.getUTCSeconds());
        }

        /**
         * Get the svg elements
         */
        var groundStart = this._svg.querySelector("#_x30_1_x5F_ground") as SVGGraphicsElement;
        var groundSearch = this._svg.querySelector("#_x30_2_x5F_ground_3_") as SVGGraphicsElement;
        var groundFound = this._svg.querySelector("#_x30_3_x5F_ground_3_") as SVGGraphicsElement;
        var groundCamp = this._svg.querySelector("#_x30_4_x5F_ground_3_") as SVGGraphicsElement;
        var groundWater = this._svg.querySelector("#_x30_5_x5F_ground") as SVGGraphicsElement;
        var groundEnd = this._svg.querySelector("#_x30_6_x5F_ground") as SVGGraphicsElement;
        var ship = this._svg.querySelector("#ship") as SVGGraphicsElement;
        var airplane = this._svg.querySelector("#airplane") as SVGGraphicsElement;
        var corona = this._svg.querySelector("#corona") as SVGGraphicsElement;
        var stars = this._svg.querySelector("#stars") as SVGGraphicsElement;
        var sun = this._svg.querySelector("#sun") as SVGCircleElement;
        var moon = this._svg.querySelector("#moon_4_") as SVGCircleElement;
        var cloud1 = this._svg.querySelector("#cloud1") as SVGCircleElement;
        var cloud2 = this._svg.querySelector("#cloud2") as SVGCircleElement;

        var stopsHeaven: SVGLinearGradientElement[] = [];
        stopsHeaven[0] = this._svg.querySelector("#sky_1_ > stop:nth-child(1)") as SVGLinearGradientElement;
        stopsHeaven[1] = this._svg.querySelector("#sky_1_ > stop:nth-child(2)") as SVGLinearGradientElement;
        stopsHeaven[2] = this._svg.querySelector("#sky_1_ > stop:nth-child(3)") as SVGLinearGradientElement;
        stopsHeaven[3] = this._svg.querySelector("#sky_1_ > stop:nth-child(4)") as SVGLinearGradientElement;

        var stopsWater: SVGLinearGradientElement[] = [];
        stopsWater[0] = this._svg.querySelector("#water_2_ > stop:nth-child(1)") as SVGLinearGradientElement;
        stopsWater[1] = this._svg.querySelector("#water_2_ > stop:nth-child(2)") as SVGLinearGradientElement;
        stopsWater[2] = this._svg.querySelector("#water_2_ > stop:nth-child(3)") as SVGLinearGradientElement;
        stopsWater[3] = this._svg.querySelector("#water_2_ > stop:nth-child(4)") as SVGLinearGradientElement;

        var mountain2 = this._svg.querySelector("#montain2") as SVGPolygonElement;
        var mountain3 = this._svg.querySelector("#montain3") as SVGPolygonElement;



        var pos = this._data.getLatLng(date, "mosaic");
        var expeditionPhase = this._data.getExpeditionPhase(date);
        var values = this._sunCalc.calc(date, pos.lat, pos.lng);

        /**
         * Get day-phase and phase-ratio.
         */
        var phaseRatio = values.ratio;
        var dayPhase = values.phase;

        /**
         * Debug output
         */
        // this._debug.textContent = DateUtil.toString(date) + " " + e5.Text.fillHead(date.getHours() + "", 2, "0") + ":" + e5.Text.fillHead(date.getMinutes() + "", 2, "0") + " " + pos.lat.toFixed(1) + "/" + pos.lng.toFixed(1) + " " + dayPhase + " " + Math.round(phaseRatio * 100) + "%" + (values.solstice ? " solstice" : "");

        /**
         * -----------------
         * Apply to elements.
         * -----------------
         */

        /**
        * Set milestone phase
        */
        groundStart.classList.toggle("hidden", expeditionPhase !== "start");
        groundSearch.classList.toggle("hidden", expeditionPhase !== "search" && expeditionPhase !== "departure");
        groundFound.classList.toggle("hidden", expeditionPhase !== "found");
        groundCamp.classList.toggle("hidden", expeditionPhase !== "camp");
        groundWater.classList.toggle("hidden", expeditionPhase !== "departure-open-water");
        groundEnd.classList.toggle("hidden", expeditionPhase !== "end");

        /**
         * Show/Hide the supply unit (ship, plane, corona, none).
         */
        var supplyUnitKind = this._data.getSupplyUnit(date);
        if (supplyUnitKind === "plane") {
            airplane.classList.toggle("hidden", false);
            ship.classList.toggle("hidden", true);
            if (corona)
                corona.classList.toggle("hidden", true);
        } else if (supplyUnitKind === "ship") {
            ship.classList.toggle("hidden", false);
            airplane.classList.toggle("hidden", true);
            if (corona)
                corona.classList.toggle("hidden", true);
        } else if (supplyUnitKind === "corona") {
            ship.classList.toggle("hidden", true);
            airplane.classList.toggle("hidden", true);
            if (corona)
                corona.classList.toggle("hidden", false);
        } else if (supplyUnitKind === "none") {
            ship.classList.toggle("hidden", true);
            airplane.classList.toggle("hidden", true);
            if (corona)
                corona.classList.toggle("hidden", true);
        }

        /**
         * Set sun and moon position
         */
        var altitudeFactor = 320;
        var azimuthFactor = 190;

        var sunPositionX = ((values.sun.azimuth * 180 / Math.PI) / 180) * -azimuthFactor;
        var sunPositionY = ((values.sun.altitude * 180 / Math.PI) / 90) * -altitudeFactor;
        sun.style.transform = `translate(${sunPositionX}px, ${sunPositionY}px)`;

        var moonPositionX = ((values.moon.azimuth * 180 / Math.PI) / 180) * -azimuthFactor;
        var moonPositionY = ((values.moon.altitude * 180 / Math.PI) / 90) * -altitudeFactor;
        moon.style.transform = `translate(${moonPositionX}px, ${moonPositionY}px)`;

        /**
         * Set colors
         */
        if (dayPhase === "sunrise") {
            var r = phaseRatio * 2 - 1;
            stars.style.opacity = (1 - phaseRatio) + "";

            if (phaseRatio < 0.5) {
                r *= -1;
                mountain2.style.fill = Color.interpolate("#B38077", "#232F5B", r);
                mountain3.style.fill = Color.interpolate("#DF9577", "#293968", r);

                for (var i = 0; i < 4; ++i) {
                    stopsHeaven[i].style.stopColor = Color.interpolate(this._colorSunrise[i], this._colorNight[i], r);
                    stopsWater[3 - i].style.stopColor = stopsHeaven[i].style.stopColor;
                }
            } else {
                mountain2.style.fill = Color.interpolate("#B38077", "#E7E7EF", r);
                mountain3.style.fill = Color.interpolate("#DF9577", "#CCD8E0", r);
                for (var i = 0; i < 4; ++i) {
                    stopsHeaven[i].style.stopColor = Color.interpolate(this._colorSunrise[i], this._colorDay[i], r);
                    stopsWater[3 - i].style.stopColor = stopsHeaven[i].style.stopColor;
                }
            }
        }
        else if (dayPhase === "day") {
            mountain2.style.fill = "#E7E7EF";
            mountain3.style.fill = "#CCD8E0";
            stars.style.opacity = "0";
            for (var i = 0; i < 4; ++i) {
                stopsHeaven[i].style.stopColor = this._colorDay[i];
                stopsWater[3 - i].style.stopColor = stopsHeaven[i].style.stopColor;
            }
        }
        else if (dayPhase === "sunset") {
            var r = phaseRatio * 2 - 1;
            stars.style.opacity = (phaseRatio) + "";
            if (phaseRatio < 0.5) {
                r *= -1;
                mountain2.style.fill = Color.interpolate("#67699A", "#E7E7EF", r);
                mountain3.style.fill = Color.interpolate("#9684AB", "#CCD8E0", r);
                for (var i = 0; i < 4; ++i) {
                    stopsHeaven[i].style.stopColor = Color.interpolate(this._colorSunset[i], this._colorDay[i], r);
                    stopsWater[3 - i].style.stopColor = stopsHeaven[i].style.stopColor;
                }
            } else {
                mountain2.style.fill = Color.interpolate("#67699A", "#232F5B", r);
                mountain3.style.fill = Color.interpolate("#9684AB", "#293968", r);
                for (var i = 0; i < 4; ++i) {
                    stopsHeaven[i].style.stopColor = Color.interpolate(this._colorSunset[i], this._colorNight[i], r);
                    stopsWater[3 - i].style.stopColor = stopsHeaven[i].style.stopColor;
                }
            }
        }
        else if (dayPhase === "night") {
            mountain2.style.fill = "#232F5B";
            mountain3.style.fill = "#293968";
            stars.style.opacity = "1";
            for (var i = 0; i < 4; ++i) {
                stopsHeaven[i].style.stopColor = this._colorNight[i];
                stopsWater[3 - i].style.stopColor = stopsHeaven[i].style.stopColor;
            }
        }

        /**
         * Cloud css-animation
         */
        var windValue = e5.Calc.clamp(Math.abs(this._data.getWind(date)), 0, 34);
        var duration = Math.round(34 - windValue);
        cloud1.style.animationDuration = duration + "s";

        //Seek to immediately ???
        cloud1.style.animationDelay = -(duration * 0.5) + "s";
        //TODO: Warum eine Zeitverzögerung ??? Randomness ???
        // cloud1.style.animationDelay = (duration * 0.5) + "s";

        cloud2.style.animationDuration = duration + "s";
        // cloud1.classList.toggle('windless', windValue < 0.278);
        // cloud1.classList.toggle('windy', windValue > 8 && windValue < 20);
        // cloud1.classList.toggle('stormy', windValue >= 20);
        // cloud2.classList.toggle('windless', windValue < 0.278);
        // cloud2.classList.toggle('windy', windValue > 8 && windValue < 20);
        // cloud2.classList.toggle('stormy', windValue >= 20);
    }
}